import type { UserRole, WorkflowCapabilityId } from '@statura/types';
import { getWorkflowCapabilityDefinition } from '@statura/types';
import { normalizeRole } from './rbac';

type InternalRole = Exclude<UserRole, 'CLIENT' | 'FAMILY_CARER'>;

const PLATFORM_ADMIN_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
] as const satisfies readonly InternalRole[];
const REGION_OPERATION_ROLES = ['REGION_MANAGER'] as const satisfies readonly InternalRole[];
const FACILITY_OPERATION_ROLES = ['FACILITY_MANAGER'] as const satisfies readonly InternalRole[];
const REGION_COMPLIANCE_ROLES = [
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
] as const satisfies readonly InternalRole[];
const REGION_FINANCE_ROLES = [
  'REGION_MANAGER',
  'REGION_FINANCE_OFFICER',
] as const satisfies readonly InternalRole[];
const REGION_ANALYTICS_ROLES = [
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
] as const satisfies readonly InternalRole[];
const CARE_COORDINATION_ROLES = [
  'COORDINATOR',
  'CASE_MANAGER',
] as const satisfies readonly InternalRole[];
const CLINICAL_REVIEW_ROLES = [
  'CLINICAL_LEAD',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
] as const satisfies readonly InternalRole[];
const FRONTLINE_WORKER_ROLES = [
  'CARE_WORKER',
  'AGENCY_CONTRACTOR',
] as const satisfies readonly InternalRole[];
const ALLIED_SUPPORT_ROLES = [
  'ALLIED_HEALTH',
  'LIFESTYLE_OFFICER',
] as const satisfies readonly InternalRole[];
const FINANCE_ROLES = [
  'FINANCE_OFFICER',
  'PAYROLL_OFFICER',
] as const satisfies readonly InternalRole[];

function uniqueRoles(...groups: readonly (readonly InternalRole[])[]): InternalRole[] {
  return [...new Set(groups.flatMap((group) => [...group]))];
}

function intersectRoles(
  routeRoles: readonly InternalRole[],
  actionRoles: readonly InternalRole[],
): InternalRole[] {
  return routeRoles.filter((role) => actionRoles.includes(role));
}

export const WORKFLOW_CAPABILITY_ROLE_MAP = {
  'tenant.configure': uniqueRoles(PLATFORM_ADMIN_ROLES),
  // Client create/edit gates are reconciled with the regulated RPC allowlists so
  // the UI is not silently stricter than the database (which is the regulated
  // source of truth). `create` mirrors register_client_controlled (admins +
  // COORDINATOR) plus CASE_MANAGER — the SAH role that owns a client's case.
  // `edit` mirrors update_client_controlled's regulated_actor_context allowlist
  // exactly. See migration *_client_create_allow_case_manager.sql.
  'client.create': uniqueRoles(PLATFORM_ADMIN_ROLES, ['COORDINATOR', 'CASE_MANAGER']),
  'client.edit': uniqueRoles(PLATFORM_ADMIN_ROLES, [
    'OWNER',
    'COORDINATOR',
    'CASE_MANAGER',
    'FACILITY_MANAGER',
    'REGION_MANAGER',
    'CLINICAL_LEAD',
    'REGISTERED_NURSE',
  ]),
  'client.care.workspace': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    REGION_OPERATION_ROLES,
    FACILITY_OPERATION_ROLES,
    CARE_COORDINATION_ROLES,
    CLINICAL_REVIEW_ROLES,
    FRONTLINE_WORKER_ROLES,
    ALLIED_SUPPORT_ROLES,
  ),
  'frontline.shift.execute': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    ['COORDINATOR'],
    CLINICAL_REVIEW_ROLES,
    FRONTLINE_WORKER_ROLES,
    ['ALLIED_HEALTH'],
  ),
  'frontline.handover.access': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    ['COORDINATOR', 'FACILITY_MANAGER'],
    CLINICAL_REVIEW_ROLES,
    FRONTLINE_WORKER_ROLES,
  ),
  'frontline.handover.signoff': uniqueRoles(PLATFORM_ADMIN_ROLES, [
    'COORDINATOR',
    'FACILITY_MANAGER',
    'CLINICAL_LEAD',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
  ]),
  'frontline.observation.record': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    ['COORDINATOR'],
    CLINICAL_REVIEW_ROLES,
    FRONTLINE_WORKER_ROLES,
    ['ALLIED_HEALTH'],
  ),
  'frontline.medication.administer': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    ['COORDINATOR'],
    CLINICAL_REVIEW_ROLES,
    ['CARE_WORKER'],
  ),
  'frontline.wound.record': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    ['COORDINATOR'],
    CLINICAL_REVIEW_ROLES,
    ['ALLIED_HEALTH'],
  ),
  'incident.create': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    CARE_COORDINATION_ROLES,
    CLINICAL_REVIEW_ROLES,
    FRONTLINE_WORKER_ROLES,
    ['ALLIED_HEALTH', 'FACILITY_MANAGER', 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
    REGION_COMPLIANCE_ROLES,
  ),
  'incident.review': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    CARE_COORDINATION_ROLES,
    CLINICAL_REVIEW_ROLES,
    ['ALLIED_HEALTH', 'FACILITY_MANAGER', 'QUALITY_OFFICER', 'COMPLIANCE_OFFICER'],
    REGION_COMPLIANCE_ROLES,
    ['QUALITY_DIRECTOR', 'CLINICAL_DIRECTOR', 'OPERATIONS_DIRECTOR', 'CEO', 'OWNER'],
  ),
  'complaint.create': uniqueRoles(PLATFORM_ADMIN_ROLES, REGION_COMPLIANCE_ROLES, [
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'REGISTERED_NURSE',
  ]),
  'service.delivery.manage': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    REGION_OPERATION_ROLES,
    CARE_COORDINATION_ROLES,
    ['CLINICAL_LEAD'],
  ),
  'service.execution.reconcile': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    REGION_OPERATION_ROLES,
    FACILITY_OPERATION_ROLES,
    CARE_COORDINATION_ROLES,
    ['CLINICAL_LEAD'],
  ),
  'roster.manage': uniqueRoles(PLATFORM_ADMIN_ROLES, [
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CLINICAL_LEAD',
    'TEAM_LEADER',
  ]),
  'sah.claim.prepare': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    FINANCE_ROLES,
    REGION_FINANCE_ROLES,
    CARE_COORDINATION_ROLES,
    ['CLINICAL_LEAD'],
  ),
  'sah.statement.issue': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    FINANCE_ROLES,
    REGION_FINANCE_ROLES,
    CARE_COORDINATION_ROLES,
  ),
  'sah.athm.manage': uniqueRoles(
    PLATFORM_ADMIN_ROLES,
    FINANCE_ROLES,
    REGION_FINANCE_ROLES,
    CARE_COORDINATION_ROLES,
    ['CLINICAL_LEAD'],
  ),
  'workforce.payroll.review': uniqueRoles(
    // Payroll case review is read-only; roster follow-up stays behind roster.manage.
    PLATFORM_ADMIN_ROLES,
    FINANCE_ROLES,
    REGION_FINANCE_ROLES,
    ['COORDINATOR', 'FACILITY_MANAGER', 'TEAM_LEADER'],
  ),
  'prudential.access': uniqueRoles(PLATFORM_ADMIN_ROLES, FINANCE_ROLES, REGION_FINANCE_ROLES, [
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'COORDINATOR',
    'CLINICAL_LEAD',
  ]),
  'quality.gpms.access': uniqueRoles(PLATFORM_ADMIN_ROLES, REGION_COMPLIANCE_ROLES, [
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'CLINICAL_LEAD',
  ]),
  'dex.reporting': uniqueRoles(PLATFORM_ADMIN_ROLES, REGION_COMPLIANCE_ROLES, [
    'COORDINATOR',
    'CLINICAL_LEAD',
    'QUALITY_OFFICER',
    'COMPLIANCE_OFFICER',
  ]),
  'analytics.access': uniqueRoles(PLATFORM_ADMIN_ROLES, REGION_ANALYTICS_ROLES, [
    'COORDINATOR',
    'CLINICAL_LEAD',
    'FINANCE_OFFICER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ]),
  'portal.care.view': ['CLIENT', 'FAMILY_CARER'],
  'portal.safety.view': ['CLIENT', 'FAMILY_CARER'],
  'portal.financial.view': ['CLIENT', 'FAMILY_CARER'],
  'portal.messaging': ['CLIENT', 'FAMILY_CARER'],
  'portal.feedback': ['CLIENT', 'FAMILY_CARER'],
  'portal.documents.view': ['CLIENT', 'FAMILY_CARER'],
  'portal.rights.view': ['CLIENT', 'FAMILY_CARER'],
} as const satisfies Record<WorkflowCapabilityId, readonly UserRole[]>;

export function getAllowedRolesForWorkflowCapability(
  capabilityId: WorkflowCapabilityId,
): readonly UserRole[] {
  return WORKFLOW_CAPABILITY_ROLE_MAP[capabilityId];
}

function hasAllowedRole(role: string | null | undefined, allowedRoles: readonly string[]): boolean {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return false;
  return allowedRoles.includes(normalizedRole);
}

export function canAccessWorkflowCapability(
  role: string | null | undefined,
  capabilityId: WorkflowCapabilityId,
): boolean {
  return hasAllowedRole(role, getAllowedRolesForWorkflowCapability(capabilityId));
}

export function listWorkflowCapabilitiesForRole(
  role: string | null | undefined,
): WorkflowCapabilityId[] {
  if (!normalizeRole(role)) return [];
  return (Object.keys(WORKFLOW_CAPABILITY_ROLE_MAP) as WorkflowCapabilityId[]).filter(
    (capabilityId) => canAccessWorkflowCapability(role, capabilityId),
  );
}

export const CLIENT_CREATE_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('client.create');
export const CLIENT_EDIT_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('client.edit');
export const CLIENT_CARE_WORKSPACE_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('client.care.workspace');
export const CLIENT_ASSESSMENT_ALLOWED_ROLES = uniqueRoles(
  CLIENT_CARE_WORKSPACE_ALLOWED_ROLES as readonly InternalRole[],
  ['QUALITY_OFFICER'],
);
export const STAFF_CREATE_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'OWNER',
  'CEO',
  'OPERATIONS_DIRECTOR',
  'HR_DIRECTOR',
  'HR_OFFICER',
  'FACILITY_MANAGER',
  'REGION_MANAGER',
  'COORDINATOR',
]);
export const STAFF_WORKSPACE_ALLOWED_ROLES = uniqueRoles(STAFF_CREATE_ALLOWED_ROLES, [
  'REGION_COMPLIANCE_OFFICER',
  'CLINICAL_LEAD',
  'TEAM_LEADER',
]);
export const DEX_REPORTING_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('dex.reporting');
export const INCIDENT_CREATE_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('incident.create');
export const FRONTLINE_INCIDENT_REVIEW_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('incident.review');
export const COMPLAINT_CREATE_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('complaint.create');
export const FINANCE_ACCESS_ALLOWED_ROLES = uniqueRoles(
  PLATFORM_ADMIN_ROLES,
  FINANCE_ROLES,
  REGION_FINANCE_ROLES,
);
/** Roles accepted by create_invoice_controlled for a manually prepared invoice. */
export const MANUAL_INVOICE_CREATE_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'FINANCE_OFFICER',
]);
/** Roles accepted by generate_private_client_invoices_controlled. */
export const PRIVATE_INVOICE_GENERATE_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'OWNER',
  'FINANCE_OFFICER',
]);
/** Roles accepted by record_private_care_rate_controlled. */
export const PRIVATE_CARE_QUOTE_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'FINANCE_OFFICER',
]);
/** Roles accepted by the NDIS invoice issue, receipt and correction actions. */
export const NDIS_INVOICE_ACTION_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'FINANCE_OFFICER',
]);
/** Roles accepted by the controlled draft-invoice combine and split actions. */
export const INVOICE_GROUPING_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'OWNER',
  'FINANCE_OFFICER',
]);
/** Finance > Invoices Help must satisfy both the route and the named leaf-action guard. */
export const FINANCE_INVOICE_CREATE_ALLOWED_ROLES = intersectRoles(
  FINANCE_ACCESS_ALLOWED_ROLES,
  MANUAL_INVOICE_CREATE_ALLOWED_ROLES,
);
export const FINANCE_PRIVATE_INVOICE_GENERATE_ALLOWED_ROLES = intersectRoles(
  FINANCE_ACCESS_ALLOWED_ROLES,
  PRIVATE_INVOICE_GENERATE_ALLOWED_ROLES,
);
export const FINANCE_NDIS_INVOICE_ACTION_ALLOWED_ROLES = intersectRoles(
  FINANCE_ACCESS_ALLOWED_ROLES,
  NDIS_INVOICE_ACTION_ALLOWED_ROLES,
);
export const FINANCE_INVOICE_GROUPING_ALLOWED_ROLES = intersectRoles(
  FINANCE_ACCESS_ALLOWED_ROLES,
  INVOICE_GROUPING_ALLOWED_ROLES,
);
/** Roles admitted by the Finance > Billing period-review surface. */
export const BILLING_RUN_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'OWNER',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'FINANCE_OFFICER',
  'REGION_FINANCE_OFFICER',
]);
export const FINANCE_CLAIMS_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('sah.claim.prepare');
export const FINANCE_CONTRIBUTIONS_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('sah.statement.issue');
export const SAH_CLAIM_PREP_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('sah.claim.prepare');
export const SAH_STATEMENT_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('sah.statement.issue');
export const SAH_READINESS_ALLOWED_ROLES = uniqueRoles(
  SAH_STATEMENT_ALLOWED_ROLES as readonly InternalRole[],
  ['OWNER'],
);
export const SAH_ATHM_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('sah.athm.manage');
export const SAH_EVIDENCE_OVERSIGHT_ALLOWED_ROLES = uniqueRoles(
  PLATFORM_ADMIN_ROLES,
  REGION_COMPLIANCE_ROLES,
);
export const SERVICE_DELIVERY_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('service.delivery.manage');
export const SERVICE_EXECUTION_RECONCILIATION_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability(
  'service.execution.reconcile',
);
// Exact intersection of the reconciliation route cohort and the legacy
// complete_service_execution / supersede_service_execution_evidence RPC
// allowlists. The queue is visible to the wider reconciliation cohort, but
// only these roles may be shown or invoke the web evidence fallback until the
// evidence RPCs themselves are replaced by a CPC-specific actor boundary.
export const SERVICE_EXECUTION_EVIDENCE_WRITER_ALLOWED_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
] as const;
// Desk complete of a SAH visit (`complete_sah_service_delivery` manager
// override). TEAM_LEADER / FACILITY_MANAGER close forgotten clocks; they are
// not on service.delivery.manage (create / live clock-in stay coordinator).
export const SAH_VISIT_COMPLETE_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'FACILITY_MANAGER',
  'COORDINATOR',
  'TEAM_LEADER',
  'CASE_MANAGER',
]);
// Mirrors EXACTLY the regulated_actor_context allowlist of
// approve_sah_delivery_readiness_override_controlled (CP4 SAH roster service
// bridge). A role shown the web override affordance must be accepted by the RPC.
export const SAH_DELIVERY_READINESS_OVERRIDE_ALLOWED_ROLES = uniqueRoles(
  PLATFORM_ADMIN_ROLES, // SYSTEM_ADMIN, TENANT_ADMIN
  ['FACILITY_MANAGER', 'COORDINATOR', 'TEAM_LEADER', 'CASE_MANAGER', 'CLINICAL_LEAD'] as const,
);
// Mirrors approve_sah_service_financials_override_controlled. Same delivery
// cohort plus finance officers — GB-sized providers do not have a separate
// finance desk for every visit.
export const SAH_FINANCIALS_OVERRIDE_ALLOWED_ROLES = uniqueRoles(
  SAH_DELIVERY_READINESS_OVERRIDE_ALLOWED_ROLES,
  ['FINANCE_OFFICER', 'REGION_FINANCE_OFFICER'] as const,
);
// Supported web cutover cohort. Keep this exact with the controlled SQL RPC;
// it is deliberately narrower than ordinary service-delivery mutation roles.
export const SAH_WP1B_CUTOVER_MANAGER_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
] as const satisfies readonly InternalRole[];
// Mirrors EXACTLY the regulated_actor_context allowlist of
// confirm_sah_service_delivery (migration
// 20267900000000_confirm_sah_service_delivery_lifecycle_guards). Keep in lockstep
// with that SQL: a role gated in here but absent from the RPC allowlist would see
// a Confirm affordance the RPC then denies, and vice-versa. Confirming a delivered
// visit is an operational/clinical attestation — finance + region-finance roles
// (which may VIEW the reconciliation page via sah.statement.issue) are
// intentionally excluded, matching the RPC.
export const SAH_SERVICE_CONFIRMATION_ROLES = uniqueRoles(
  PLATFORM_ADMIN_ROLES, // SYSTEM_ADMIN, TENANT_ADMIN
  CARE_COORDINATION_ROLES, // COORDINATOR, CASE_MANAGER
  ['FACILITY_MANAGER', 'TEAM_LEADER', 'CARE_WORKER', 'REGISTERED_NURSE', 'ENROLLED_NURSE'] as const,
);
export const FRONTLINE_SHIFT_EXECUTION_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('frontline.shift.execute');
export const FRONTLINE_HANDOVER_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability(
  'frontline.handover.access',
);
export const FRONTLINE_HANDOVER_SIGNOFF_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability(
  'frontline.handover.signoff',
);
export const FRONTLINE_OBSERVATION_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability(
  'frontline.observation.record',
);
export const FRONTLINE_MEDICATION_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability(
  'frontline.medication.administer',
);
export const S8_DISCREPANCY_RESOLUTION_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, [
  'COORDINATOR',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
]);
export const FRONTLINE_WOUND_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('frontline.wound.record');
export const ROSTER_MANAGE_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('roster.manage');
export const WORKFORCE_PAYROLL_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability(
  'workforce.payroll.review',
);
export const ANALYTICS_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('analytics.access');
export const PRUDENTIAL_ALLOWED_ROLES = getAllowedRolesForWorkflowCapability('prudential.access');
export const PRUDENTIAL_DEPOSIT_ALLOWED_ROLES = uniqueRoles(
  PLATFORM_ADMIN_ROLES,
  FINANCE_ROLES,
  REGION_FINANCE_ROLES,
  ['FACILITY_MANAGER', 'QUALITY_OFFICER'],
);
export const PRUDENTIAL_WRITE_ALLOWED_ROLES = uniqueRoles(PLATFORM_ADMIN_ROLES, FINANCE_ROLES, [
  'FACILITY_MANAGER',
  'QUALITY_OFFICER',
]);
export const QUALITY_GPMS_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('quality.gpms.access');
export const TENANT_CONFIGURATION_ALLOWED_ROLES =
  getAllowedRolesForWorkflowCapability('tenant.configure');

export function canCreateClientRecords(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'client.create');
}

export function canEditClientRecords(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'client.edit');
}

export function canAccessDexReporting(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'dex.reporting');
}

/**
 * Roles that may CREATE, UPDATE or DELETE a document.
 *
 * This mirrors the `documents_manager_insert` / `_update` / `_delete` RLS policies exactly.
 * Those policies gate on `can_manage_care_plan_lifecycle('edit') OR can_manage_roster_lifecycle()`,
 * whose live cohorts are:
 *
 *   care plan lifecycle : CASE_MANAGER, CLINICAL_LEAD, COORDINATOR, FACILITY_MANAGER,
 *                         REGISTERED_NURSE, SYSTEM_ADMIN, TENANT_ADMIN
 *   roster lifecycle    : COORDINATOR, FACILITY_MANAGER, SYSTEM_ADMIN, TEAM_LEADER, TENANT_ADMIN
 *
 * WHY THIS EXISTS. Document write had no capability of its own, so the UI had nothing to gate
 * on and used `canManageDocumentFolders` instead. That is a DIFFERENT cohort — it matches the
 * folder policy exactly and is correct for folders — so four roles that may manage folders but
 * may not write documents (COMPLIANCE_OFFICER, QUALITY_OFFICER, REGION_COMPLIANCE_OFFICER,
 * REGION_MANAGER) were shown a drag-to-upload affordance the database then refused, and
 * TEAM_LEADER, who may write, was not offered it at all.
 *
 * Write permission being derived from care-plan or roster capability is itself incidental —
 * a document permission would be better modelling. That is a product decision; this constant
 * records the rule as it IS ENFORCED so the interface stops disagreeing with the database.
 * `document-rbac-parity.test.ts` fails if the two drift again.
 */
const DOCUMENT_WRITE_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'REGISTERED_NURSE',
  'TEAM_LEADER',
] as const satisfies readonly InternalRole[];

/** True when this role may create, update or delete a document. */
export function canWriteDocuments(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (DOCUMENT_WRITE_ROLES as readonly string[]).includes(normalized);
}

export { DOCUMENT_WRITE_ROLES };

export function canCreateIncidentRecords(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'incident.create');
}

export function canReviewIncidentRecords(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'incident.review');
}

// S2 keeps the existing DB writer cohorts. D2 may expand notes independently.
const INCIDENT_QUALITY_STANDARD_EDIT_ROLES = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'FACILITY_MANAGER',
  'COORDINATOR',
  'CASE_MANAGER',
  'CLINICAL_LEAD',
  'TEAM_LEADER',
  'REGISTERED_NURSE',
  'ENROLLED_NURSE',
  'ALLIED_HEALTH',
  'QUALITY_OFFICER',
  'REGION_MANAGER',
  'REGION_COMPLIANCE_OFFICER',
] as const;

// D2 / Paul decision 9: notes admit CO; standards retain their writer cohort.
const INCIDENT_NOTE_WRITE_ROLES = [
  ...INCIDENT_QUALITY_STANDARD_EDIT_ROLES,
  'COMPLIANCE_OFFICER',
] as const;

export function canWriteIncidentNotes(role: string | null | undefined): boolean {
  return hasAllowedRole(role, INCIDENT_NOTE_WRITE_ROLES);
}

export function canEditIncidentQualityStandards(role: string | null | undefined): boolean {
  return hasAllowedRole(role, INCIDENT_QUALITY_STANDARD_EDIT_ROLES);
}

/** Compatibility for existing consumers; new notes and standards use separate gates. */
export function canContributeIncidentCollaboration(role: string | null | undefined): boolean {
  return canEditIncidentQualityStandards(role);
}

export function canCreateComplaintRecords(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'complaint.create');
}

export function canAccessFinanceWorkspace(role: string | null | undefined): boolean {
  return hasAllowedRole(role, FINANCE_ACCESS_ALLOWED_ROLES);
}

export function canPrepareSahClaims(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'sah.claim.prepare');
}

export function canIssueSahStatements(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'sah.statement.issue');
}

export function canManageSahAthm(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'sah.athm.manage');
}

export function canAccessServiceDeliveryWorkspace(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'service.delivery.manage');
}

export function canCompleteSahVisit(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_VISIT_COMPLETE_ALLOWED_ROLES as readonly string[]).includes(normalized);
}

// Whether the role may client-confirm a delivered SAH visit — gated on the exact
// confirm_sah_service_delivery RPC allowlist (see SAH_SERVICE_CONFIRMATION_ROLES).
export function canConfirmSahServiceDelivery(role: string | null | undefined): boolean {
  const normalized = normalizeRole(role);
  if (!normalized) return false;
  return (SAH_SERVICE_CONFIRMATION_ROLES as readonly string[]).includes(normalized);
}

export function canExecuteFrontlineShift(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'frontline.shift.execute');
}

export function canAccessFrontlineHandovers(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'frontline.handover.access');
}

export function canSignOffFrontlineHandovers(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'frontline.handover.signoff');
}

export function canRecordFrontlineObservations(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'frontline.observation.record');
}

export function canAdministerFrontlineMedications(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'frontline.medication.administer');
}

export function canRecordFrontlineWounds(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'frontline.wound.record');
}

export function canManageRosterWorkspace(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'roster.manage');
}

export function canAccessPayrollWorkspace(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'workforce.payroll.review');
}

export function canAccessAnalyticsWorkspace(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'analytics.access');
}

export function canAccessPrudentialWorkspace(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'prudential.access');
}

export function canManagePrudentialGovernance(role: string | null | undefined): boolean {
  return hasAllowedRole(role, PRUDENTIAL_WRITE_ALLOWED_ROLES);
}

// Refund preview is narrower than general prudential governance.
// These affordances do not replace controlled RPC actor/tenant checks.
export function canPreviewPrudentialRefund(role: string | null | undefined): boolean {
  return hasAllowedRole(role, [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'FACILITY_MANAGER',
    'FINANCE_OFFICER',
  ]);
}

// Mirrors create_prudential_refund_event_controlled; preview has a separate cohort.
export function canProcessPrudentialRefund(role: string | null | undefined): boolean {
  return hasAllowedRole(role, ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'FINANCE_OFFICER']);
}

export function canAccessQualityGpmsWorkspace(role: string | null | undefined): boolean {
  return canAccessWorkflowCapability(role, 'quality.gpms.access');
}

/**
 * Regulatory-notification corrections — who may edit an already-recorded
 * external notification (SafeWork, ACQSC appointment/cessation, OAIC, etc).
 *
 * Deliberately stricter than `incident.review` or `prudential.access`: once a
 * regulatory submission has been logged against a date/reference, amending it
 * changes the audit trail. Restricted to platform admin + the two roles with
 * explicit regulatory stewardship (Quality Officer, Facility Manager).
 *
 * Care workers and clinical staff can record first-time notifications (via
 * the existing "Record Notification" buttons) but cannot rewrite them.
 */
export const REGULATORY_NOTIFICATION_CORRECT_ROLES: readonly UserRole[] = [
  'SYSTEM_ADMIN',
  'TENANT_ADMIN',
  'QUALITY_OFFICER',
  'FACILITY_MANAGER',
];

export function canCorrectRegulatoryNotification(role: string | null | undefined): boolean {
  return hasAllowedRole(role, REGULATORY_NOTIFICATION_CORRECT_ROLES);
}

export function getWorkflowCapabilitySummary(capabilityId: WorkflowCapabilityId) {
  const definition = getWorkflowCapabilityDefinition(capabilityId);
  return {
    ...definition,
    allowedRoles: getAllowedRolesForWorkflowCapability(capabilityId),
  };
}
